import { Link } from "@/lib/router-compat";
import { ArrowRight, Check, X, HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

type CellValue = true | false | "?" | string;

const comparisons: { feature: string; us: CellValue; local: CellValue; national: CellValue }[] = [
  { feature: "In-house crews, never subcontracted", us: true, local: "?", national: false },
  { feature: "Free detailed roof inspection", us: true, local: "?", national: false },
  { feature: "Licensed & fully insured", us: true, local: "?", national: true },
  { feature: "Storm damage specialists", us: true, local: false, national: false },
  { feature: "Insurance claim assistance", us: true, local: false, national: false },
  { feature: "Premium material partnerships", us: true, local: false, national: true },
  { feature: "Local owner on every job", us: true, local: "?", national: false },
  { feature: "Workmanship warranty", us: true, local: "?", national: "?" },
  { feature: "24-hour emergency service", us: true, local: false, national: false },
];

const CellIcon = ({ value, highlight }: { value: CellValue; highlight?: boolean }) => {
  if (value === true) {
    return (
      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${highlight ? "bg-secondary/20" : "bg-muted"}`}>
        <Check className={`w-5 h-5 ${highlight ? "text-secondary" : "text-foreground"}`} strokeWidth={2.5} />
      </div>
    );
  }
  if (value === false) {
    return (
      <div className="w-8 h-8 rounded-full bg-destructive/10 flex items-center justify-center">
        <X className="w-5 h-5 text-destructive" strokeWidth={2.5} />
      </div>
    );
  }
  if (value === "?") {
    return (
      <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
        <HelpCircle className="w-5 h-5 text-muted-foreground" strokeWidth={2} />
      </div>
    );
  }
  return <span className="font-semibold text-foreground">{value}</span>;
};

export const WhyChooseUs = () => {
  return (
    <section className="py-20 bg-background">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-heading font-bold text-foreground mb-4">
            WHY CHOOSE KODIAK?
          </h2>
          <p className="text-muted-foreground text-lg max-w-xl mx-auto">
            See how we compare to other contractors across Minnesota.
          </p>
        </div>

        {/* Desktop Table */}
        <div className="hidden md:block max-w-5xl mx-auto">
          <div className="grid grid-cols-[1.5fr_1fr_1fr_1fr] items-end gap-4 mb-2 pb-4">
            <div />
            <div className="text-center">
              <p className="font-heading font-bold text-foreground text-lg">Kodiak</p>
              <p className="text-muted-foreground text-sm">Local experts</p>
            </div>
            <div className="text-center">
              <p className="font-heading font-bold text-foreground text-lg">Local Competitors</p>
              <p className="text-muted-foreground text-sm">Varies</p>
            </div>
            <div className="text-center">
              <p className="font-heading font-bold text-foreground text-lg">National Chains</p>
              <p className="text-muted-foreground text-sm">Cookie cutter</p>
            </div>
          </div>

          {comparisons.map((row, i) => (
            <div
              key={row.feature}
              className={`grid grid-cols-[1.5fr_1fr_1fr_1fr] gap-4 py-4 border-t border-border items-center ${
                i % 2 === 0 ? "bg-muted/30" : ""
              } rounded-lg px-3`}
            >
              <span className="text-foreground text-base font-medium">{row.feature}</span>
              <div className="flex justify-center"><CellIcon value={row.us} highlight /></div>
              <div className="flex justify-center"><CellIcon value={row.local} /></div>
              <div className="flex justify-center"><CellIcon value={row.national} /></div>
            </div>
          ))}
          <div className="border-t border-border" />
        </div>

        {/* Mobile Cards */}
        <div className="md:hidden space-y-3 max-w-lg mx-auto">
          {comparisons.map((row) => (
            <div key={row.feature} className="bg-card border border-border rounded-xl p-4 shadow-xs">
              <p className="text-foreground font-semibold text-sm mb-3">{row.feature}</p>
              <div className="grid grid-cols-3 gap-2">
                <div className="flex flex-col items-center gap-1">
                  <CellIcon value={row.us} highlight />
                  <span className="text-xs text-muted-foreground font-medium">Kodiak</span>
                </div>
                <div className="flex flex-col items-center gap-1">
                  <CellIcon value={row.local} />
                  <span className="text-xs text-muted-foreground">Local</span>
                </div>
                <div className="flex flex-col items-center gap-1">
                  <CellIcon value={row.national} />
                  <span className="text-xs text-muted-foreground">National</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 flex justify-center">
          <Link to="/contact">
            <Button className="font-semibold font-subheading bg-secondary hover:bg-secondary/90 text-secondary-foreground px-8">
              Get a Free Estimate <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};
